import { ListItemIcon, ListItemText, MenuItem, TextField } from '@mui/material';
import {
  FocusedSensorType,
  useWeatherStationState,
} from 'state/useWeatherStationState';
import { useTranslation } from 'react-i18next';
import { useWeatherStationPrecipitationEnabled } from 'features/weatherStations/hooks/useWeatherStationPrecipitationEnabled';
import { MapIcon } from 'features/mapping/mapping.types';
import {
  DewPointIcon,
  HumidityIcon,
  PrecipitationIcon,
  TemperatureIcon,
  WindGustIcon,
  WindSpeedIcon,
} from 'features/mapping/icons';
import { useColorScheme } from 'hooks/useColorScheme';
import { TFunction } from 'i18next';

type WeatherSensorTypeSelectorProps = {
  disabled?: boolean;
};

const getWeatherOptions = (
  t: TFunction,
): {
  value: FocusedSensorType;
  label: string;
  Icon: MapIcon;
}[] => [
  {
    value: 'windSpeed',
    label: t('weatherSensorTypeSelector.windSpeed'),
    Icon: WindSpeedIcon,
  },
  {
    value: 'windGust',
    label: t('weatherSensorTypeSelector.windGust'),
    Icon: WindGustIcon,
  },
  {
    value: 'airTemp',
    label: t('weatherSensorTypeSelector.airTemp'),
    Icon: TemperatureIcon,
  },
  {
    value: 'relativeHumidity',
    label: t('weatherSensorTypeSelector.relativeHumidity'),
    Icon: HumidityIcon,
  },
  {
    value: 'precipitation1Hr',
    label: t('weatherSensorTypeSelector.precipitation1Hr'),
    Icon: PrecipitationIcon,
  },
  {
    value: 'dewPointTemperature',
    label: t('weatherSensorTypeSelector.dewPointTemperature'),
    Icon: DewPointIcon,
  },
];

export const WeatherSensorTypeSelector = (
  props: WeatherSensorTypeSelectorProps,
): JSX.Element => {
  const { disabled } = props;
  const { focusedSensorType, setFocusedSensorType } = useWeatherStationState();
  const { t } = useTranslation();
  const isPrecipitationEnabled = useWeatherStationPrecipitationEnabled();
  const { isDarkMode } = useColorScheme();

  const selectOptions = getWeatherOptions(t).filter(
    ({ value }) => !(value === 'precipitation1Hr' && !isPrecipitationEnabled),
  );

  return (
    <TextField
      select
      id="weather-sensor-type-selector"
      value={focusedSensorType ?? ''}
      onChange={(e) =>
        setFocusedSensorType(e.target.value as FocusedSensorType)
      }
      label={t('weatherSensorTypeSelector.label')}
      fullWidth
      slotProps={{
        select: {
          sx: {
            '& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
          },
        },
      }}
      disabled={disabled}
    >
      {selectOptions.map(({ value, label, Icon }) => (
        <MenuItem key={value} value={value}>
          <ListItemIcon sx={{ minWidth: '36px' }}>
            <img
              src={Icon.data}
              alt=""
              width={24}
              height={24}
              data-testid={`${label}-icon`}
              style={{
                filter: isDarkMode ? 'invert(100%)' : undefined,
              }}
            />
          </ListItemIcon>
          <ListItemText>{label}</ListItemText>
        </MenuItem>
      ))}
    </TextField>
  );
};
